import { useEffect } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { Checkbox, Paper, Select, Stack, Text, Textarea, Group, Button } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useCanOrder } from './shared/useCanOrder'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapUnspecifiedReferralToPdf } from './pdf/mapUnspecifiedReferralToPdf'
import {
  UNSPECIFIED_REFERRAL_QUESTIONNAIRE_ID,
  createInitialUnspecifiedReferralFormState,
} from './unspecifiedReferralFormData'
import type { UnspecifiedReferralFormState } from './unspecifiedReferralFormData'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { SUBMIT_PERMISSION_TOOLTIP, SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'
import { NUCC_CLASSIFICATIONS } from './shared/nuccClassifications'
import { ReferralLetterFields } from './referral/ReferralLetterFields'
import { useReferralLetterState } from './referral/useReferralLetterState'
import { useWarnBeforeUnload } from '@/hooks/useWarnBeforeUnload'
import type { ReferralSpecialtyCode } from './referral/referralTypes'

const specialtyOptions = NUCC_CLASSIFICATIONS.map((c) => ({ value: c.code, label: c.label }))

const REFERRAL_CART_ITEM: CartItem = {
  id: 'unspecified-referral',
  code: 'referral',
  name: 'Unspecified Referral',
  category: 'Referral',
}

type Props = {
  patient: Patient
  onBack: () => void
}

export const UnspecifiedReferralForm = ({ patient, onBack }: Props) => {
  const { patientDefaults, providerDefaults, insuranceDefaults, diagnosisDefaults, orgError } =
    useAutoPopulate(patient)
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<UnspecifiedReferralFormState>({
    initialValues: createInitialUnspecifiedReferralFormState(),
    validateInputOnBlur: true,
    validate: {
      referralFor: (v: string) => (!v?.trim() ? 'Referral details are required' : null),
    },
  })

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: UNSPECIFIED_REFERRAL_QUESTIONNAIRE_ID,
    generatePdfBytes: () =>
      generateBeHumanOrderPdf(mapUnspecifiedReferralToPdf(form.values, autoPopulateData)),
    buildFileName: () =>
      `Referral_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () =>
      ({
        ...form.values,
        icd10Codes: form.values.diagnosisText
          .split(/[;\n]/)
          .map((s) => s.trim().split(/\s/)[0]?.toUpperCase())
          .filter(Boolean),
      }) as unknown as Record<string, unknown>,
    vendorName: 'Referral',
    laboratoryOrganizationName: 'Unspecified Referral',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
    skipFax: true,
  })

  const referralFor = form.values.referralFor.trim()
  const hasLetter = Boolean(form.values.indication.trim() || form.values.summary.trim())
  const draftKey =
    referralFor || form.values.specialtyCode ? `${form.values.specialtyCode}|${referralFor}` : null
  const { reviewed, setReviewed, drafting, letterProps } = useReferralLetterState(
    draftKey,
    form.values.visitFindings,
  )

  // Anything entered is lost on a reload; the auto-filled signature and diagnoses are not input.
  useWarnBeforeUnload(
    !submission.orderSubmitted &&
      Boolean(
        referralFor || form.values.specialtyCode || form.values.visitFindings.trim() || hasLetter,
      ),
  )

  const handleSelectSpecialty = (code: string | null) => {
    const picked = NUCC_CLASSIFICATIONS.find((c) => c.code === code)
    form.setFieldValue('specialtyCode', picked?.code ?? '')
    form.setFieldValue('specialtyLabel', picked?.label ?? '')
    // Optional: an unspecified referral can be for a test ("Screening mammogram") with no specialty.
    if (picked && !form.values.referralFor.trim()) form.setFieldValue('referralFor', picked.label)
  }

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.providerSignatureDataUrl) {
      form.setFieldValue('providerSignatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'providerSignatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('providerSignatureDataUrl', dataUrl)
    form.setFieldValue('providerSignatureDate', date)
    signature.closeSignatureModal()
  }

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the referral before submitting.',
        color: 'red',
      })
      return
    }
    if (hasLetter && !reviewed) {
      notifications.show({
        title: 'Review required',
        message: 'Review the referral letter and clinical summary, then tick the confirmation box.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  return (
    <OrderFormLayout
      cart={<OrderCart items={[REFERRAL_CART_ITEM]} onRemove={() => {}} vendorLabel="Referral" />}
      header={
        <FormHeader
          title="Unspecified Referral"
          subtitle="Generate a referral the patient can take to any in-network provider or diagnostic center"
          onBack={onBack}
          onPreview={submission.handlePreviewPdf}
          onSubmit={handleSubmitOrder}
          exporting={submission.exporting}
          submitting={submission.submitting}
          orderSubmitted={submission.orderSubmitted}
          canSubmit={isPractitioner}
          canOrderLoading={canOrderLoading}
          hideActions
          submitTooltip="Practitioner permissions required to submit orders"
          orgError={orgError}
        />
      }
    >
      <Stack gap="lg">
        <Paper p="md" withBorder>
          <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
            REFERRAL DETAILS
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Describe the referral, e.g. &ldquo;Gastroenterologist for screening colonoscopy&rdquo;
            or &ldquo;Screening mammogram&rdquo;.
          </Text>
          <Select
            label="Specialty (optional)"
            placeholder="Select a specialty..."
            data={specialtyOptions}
            value={form.values.specialtyCode || null}
            onChange={handleSelectSpecialty}
            searchable
            clearable
            w={280}
            mb="sm"
          />
          <Textarea
            label="Referral for"
            placeholder="Gastroenterologist for screening colonoscopy"
            autosize
            minRows={2}
            withAsterisk
            {...form.getInputProps('referralFor')}
          />
          <Textarea
            label="Major complaints (optional)"
            placeholder="Primary complaint or reason prompting the referral"
            autosize
            minRows={2}
            mt="sm"
            {...form.getInputProps('majorComplaints')}
          />
        </Paper>

        {/* ICD-10 diagnosis codes (optional) */}
        <DiagnosisCodesSection
          value={form.values.diagnosisText}
          defaultCodes={diagnosisDefaults}
          onChange={(v) => form.setFieldValue('diagnosisText', v)}
        />

        <ReferralLetterFields
          draftInput={
            patient.id && draftKey
              ? {
                  patientId: patient.id,
                  kind: 'unspecified',
                  ...(form.values.specialtyCode
                    ? { specialtyCode: form.values.specialtyCode as ReferralSpecialtyCode }
                    : {}),
                  ...(referralFor ? { referralFor } : {}),
                  visitFindings: form.values.visitFindings.trim() || undefined,
                }
              : null
          }
          {...letterProps}
          autoDraft={false}
          findings={form.values.visitFindings}
          onFindingsChange={(v) => form.setFieldValue('visitFindings', v)}
          letter={form.values.indication}
          onLetterChange={(v) => form.setFieldValue('indication', v)}
          summary={form.values.summary}
          onSummaryChange={(v) => form.setFieldValue('summary', v)}
        />

        {/* Additional notes */}
        <Paper p="md" withBorder>
          <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
            ADDITIONAL NOTES
          </Text>
          <Textarea
            label="Special instructions (optional)"
            placeholder="Any additional instructions for the receiving provider"
            autosize
            minRows={2}
            {...form.getInputProps('specialInstructions')}
          />
          <Textarea
            label="Referring doctor's comments (optional)"
            placeholder="Comments from the referring provider"
            autosize
            minRows={2}
            mt="sm"
            {...form.getInputProps('referringDoctorComments')}
          />
        </Paper>

        {/* Provider signature */}
        <SignatureSection
          signatureDataUrl={form.values.providerSignatureDataUrl}
          signatureDate={form.values.providerSignatureDate}
          savedSignature={signature.savedSignature}
          signatureModalOpen={signature.signatureModalOpen}
          onOpenModal={signature.openSignatureModal}
          onCloseModal={signature.closeSignatureModal}
          onConfirm={handleSignatureConfirm}
          onSaveSignature={signature.handleSaveSignature}
          certificationText="I am a licensed healthcare provider authorizing this referral for the patient named above."
        />

        {hasLetter && (
          <Checkbox
            label="I have reviewed the referral letter and clinical summary."
            checked={reviewed}
            onChange={(e) => setReviewed(e.currentTarget.checked)}
          />
        )}

        {/* Actions */}
        <Group justify="flex-end" pb="xl">
          <Button variant="default" onClick={onBack}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={submission.handlePreviewPdf}
            loading={submission.exporting}
          >
            Download PDF
          </Button>
          <TooltipButton
            color="teal"
            loading={submission.submitting}
            disabled={
              submission.orderSubmitted ||
              !isPractitioner ||
              !form.values.providerSignatureDataUrl ||
              drafting ||
              (hasLetter && !reviewed)
            }
            disabledReason={
              !isPractitioner
                ? SUBMIT_PERMISSION_TOOLTIP
                : !form.values.providerSignatureDataUrl
                  ? SIGNATURE_REQUIRED_TOOLTIP
                  : drafting
                    ? 'Wait for the AI draft to finish'
                    : hasLetter && !reviewed
                      ? 'Review the letter and clinical summary, then tick the box above.'
                      : null
            }
            onClick={handleSubmitOrder}
          >
            {submission.orderSubmitted ? 'Referral Submitted' : 'Submit Referral'}
          </TooltipButton>
        </Group>
      </Stack>
    </OrderFormLayout>
  )
}
